import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconPlus, IconTrash } from '../components/icons';
import { Field, Modal, useAsync, useToast } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { ago, dateTime } from '../lib/format';
import { t } from '../lib/i18n';
import { ChannelChip, errText, NotificationPill } from './components';
import {
  CHANNEL_LABEL,
  ChannelAccount,
  CONSENT_LABEL,
  countryName,
  Identity,
  IDENTITY_CHANNELS,
  IDENTITY_KIND,
  NonEmailChannel,
  NotificationStatus,
  SUPPRESSION_REASON,
} from './model';
import { TelegramLinkMaker } from './TelegramLink';

interface ChannelsOfContact {
  identities: Identity[];
  notifications: { id: string; kind: string; status: NotificationStatus; stream: string; created_at: string }[];
}
interface History {
  consents: { identity_id: string; channel: string; purpose: string; status: string; source: string; proof: Record<string, unknown>; occurred_at: string; recorded_by: string; recorded_at: string }[];
  suppressions: { identity_id: string; hint: string; channel: string; scope: string; reason: string; source: string; created_at: string }[];
}

const STATUS: Record<Identity['status'], { label: string; tone: string }> = { active: { label: 'активен', tone: 'ok' }, invalid: { label: 'недействителен', tone: 'bad' }, revoked: { label: 'отозван', tone: 'cancelled' } };
const VERIFIED_BY: Record<string, string> = { otp: 'кодом', telegram_start: 'кнопкой Start', sdk: 'SDK', import: 'импортом', api: 'по API' };

/**
 * The «Каналы» block of a contact card (BUILD-SPEC §5.2): addresses by kind (masked), consent per channel and purpose
 * with its journal and proof, channel suppressions, «Добавить номер», a Telegram link, revoking an address, a manual
 * consent record with a mandatory source, and the contact's latest notifications.
 */
export function ContactChannels({ contactId, writer }: { contactId: string; writer: boolean }) {
  const toast = useToast();
  const data = useAsync(() => api.get<ChannelsOfContact>(`/contacts/${contactId}/channels`), [contactId]);
  const history = useAsync(() => api.get<History>(`/contacts/${contactId}/channel-history`), [contactId]);
  const accounts = useAsync(() => api.get<{ items: ChannelAccount[] }>('/channel-accounts'));
  const [phone, setPhone] = useState('');
  const [adding, setAdding] = useState(false);
  const [phoneNote, setPhoneNote] = useState<{ text: string; suggest?: string } | null>(null);
  const [tg, setTg] = useState(false);
  const [consentFor, setConsentFor] = useState<Identity | null>(null);
  const reload = () => {
    data.reload();
    history.reload();
  };
  const bots = (accounts.data?.items ?? []).filter((a) => a.channel === 'telegram_bot' && (a.status === 'active' || a.status === 'failing'));
  const accountName = (id: string | null) => {
    const a = (accounts.data?.items ?? []).find((x) => x.id === id);
    return a ? (typeof a.config.bot_username === 'string' ? `@${a.config.bot_username}` : a.name) : null;
  };
  async function addPhone() {
    try {
      const r = await api.post<Identity & { warnings?: { message: string }[] }>(`/contacts/${contactId}/identities`, { kind: 'phone', value: phone.trim() });
      toast(r.warnings?.[0]?.message ?? t('Номер добавлен. Для SMS, WhatsApp и Gateway нужно согласие или аттестация — запишите его.'), r.warnings ? 'bad' : 'ok');
      setPhone('');
      setPhoneNote(null);
      setAdding(false);
      reload();
    } catch (err) {
      const d = err instanceof ApiError ? err.details?.[0] : undefined;
      const fix = typeof d?.fix === 'string' ? (d.fix as string) : null;
      if (fix) {
        // A mechanical fix (spaces, dashes, a leading 00) is safe to apply: the field takes it, the person confirms.
        setPhone(fix);
        setPhoneNote({ text: t('Убрали лишние символы: {fix}. Проверьте номер и нажмите «Добавить».', { fix }) });
      } else if (d?.did_you_mean) {
        setPhoneNote({ text: t('{message}. Может быть, {suggest}?', { message: d.message, suggest: d.did_you_mean }), suggest: d.did_you_mean });
      } else {
        setPhoneNote({ text: errText(err) });
      }
    }
  }
  async function revoke(i: Identity) {
    if (!confirm(t('Отозвать адрес {hint}? Шаги каналов на него перестанут уходить; подавления не меняются.', { hint: i.hint }))) return;
    try {
      await api.del(`/contacts/${contactId}/identities/${i.id}`);
      reload();
    } catch (err) {
      toast(errText(err), 'bad');
    }
  }
  const ids = data.data?.identities ?? [];
  return (
    <div className="card">
      <div className="card-head" style={{ flexWrap: 'wrap', rowGap: 8 }}>
        <h3>{t('Каналы')}</h3>
        {writer && (
          <div className="row wrap" style={{ gap: 6 }}>
            <button className="btn btn-sm" onClick={() => setAdding((a) => !a)}><IconPlus size={14} /> {t('Номер')}</button>
            {bots.length > 0 && <button className="btn btn-sm" onClick={() => setTg((t) => !t)}>{t('Ссылка для Telegram')}</button>}
          </div>
        )}
      </div>
      <div className="card-pad stack">
        {adding && (
          <div className="ch-inline">
            <input className="input input-mono" inputMode="tel" placeholder="+37120000000" value={phone} onChange={(e) => { setPhone(e.target.value); setPhoneNote(null); }} aria-label={t('Телефон в формате E.164')} aria-describedby={phoneNote ? 'contact-phone-note' : undefined} />
            <button className="btn btn-sm btn-primary" disabled={phone.trim().length < 6} onClick={addPhone}>{t('Добавить')}</button>
          </div>
        )}
        {adding && phoneNote && (
          <div id="contact-phone-note" className="small muted" role="status">
            {phoneNote.text}{' '}
            {phoneNote.suggest && <button className="btn btn-sm btn-ghost" onClick={() => { setPhone(phoneNote.suggest!); setPhoneNote(null); }}>{t('Подставить')}</button>}
          </div>
        )}
        {tg && <TelegramLinkMaker contactId={contactId} bots={bots} />}
        {!ids.length ? (
          <div className="small muted">{data.loading ? t('Загружаю…') : t('Адресов в каналах нет. Телефон добавляется здесь или по API; Telegram, push и Web Push — только привязкой самим человеком.')}</div>
        ) : (
          ids.map((i) => {
            const st = STATUS[i.status];
            return (
              <div key={i.id} className="stack" style={{ gap: 6, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                <div className="row wrap" style={{ gap: 6 }}>
                  <b className="small">{t(IDENTITY_KIND[i.kind])}</b>
                  <code className="cell-wrap">{i.hint}</code>
                  {i.country && <span className="small muted">{countryName(i.country)}</span>}
                  <span className={`pill ${st.tone}`}>{t(st.label)}</span>
                  {i.verified_at ? <span className="pill ok" title={dateTime(i.verified_at)}>{t('проверен {how}', { how: t(VERIFIED_BY[i.verified_by ?? ''] ?? '') })}</span> : <span className="pill cancelled">{t('не проверен')}</span>}
                  {accountName(i.account) && <span className="small muted">{accountName(i.account)}</span>}
                  <span className="spacer" />
                  {writer && i.status === 'active' && <button className="btn btn-sm btn-ghost" onClick={() => setConsentFor(i)}>{t('Согласие')}</button>}
                  {writer && i.status !== 'revoked' && <button className="btn btn-sm btn-ghost" aria-label={t('Отозвать {hint}', { hint: i.hint })} onClick={() => revoke(i)}><IconTrash size={14} /></button>}
                </div>
                <div className="row wrap" style={{ gap: 6 }}>
                  {Object.entries(i.consents ?? {}).map(([ch, c]) => (
                    <span key={ch} className="row wrap" style={{ gap: 4 }}>
                      <ChannelChip channel={ch as NonEmailChannel} />
                      <span className={`pill ${CONSENT_LABEL[c.transactional]?.tone ?? ''}`} title={t('Сервисные')}>{t('серв.: {status}', { status: t(CONSENT_LABEL[c.transactional]?.label ?? c.transactional) })}</span>
                      <span className={`pill ${CONSENT_LABEL[c.marketing]?.tone ?? ''}`} title={t('Маркетинг')}>{t('марк.: {status}', { status: t(CONSENT_LABEL[c.marketing]?.label ?? c.marketing) })}</span>
                      {c.suppressed && <span className="pill bad">{t('подавлен')}</span>}
                    </span>
                  ))}
                </div>
                {Object.keys(i.unavailable ?? {}).length > 0 && <div className="small muted">{t('Недоступен в:')} {Object.entries(i.unavailable).map(([c, u]) => `${t(CHANNEL_LABEL[c as NonEmailChannel] ?? c)} (${u.reason}, ${ago(u.at)})`).join(', ')}</div>}
              </div>
            );
          })
        )}
        {(history.data?.suppressions.length ?? 0) > 0 && (
          <div className="callout bad small">
            <div>
              <b>{t('Подавления каналов:')}</b>{' '}
              {history.data!.suppressions.map((s) => `${t(CHANNEL_LABEL[s.channel as NonEmailChannel] ?? s.channel)} ${s.hint} — ${s.scope === 'all' ? t('всё') : t('маркетинг')} (${t(SUPPRESSION_REASON[s.reason] ?? s.reason)})`).join('; ')}. {t('Подавление сильнее согласия.')}
            </div>
          </div>
        )}
        {(history.data?.consents.length ?? 0) > 0 && (
          <details>
            <summary className="small" style={{ cursor: 'pointer', fontWeight: 700 }}>{t('Журнал согласий по каналам · {n}', { n: history.data!.consents.length })}</summary>
            <div className="stack" style={{ gap: 6, marginTop: 8 }}>
              {history.data!.consents.map((c, k) => (
                <details key={k} className="small" style={{ paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                  <summary className="row wrap" style={{ gap: 6, cursor: 'pointer' }}>
                    <span className={`pill ${CONSENT_LABEL[c.status]?.tone ?? ''}`}>{t(CONSENT_LABEL[c.status]?.label ?? c.status)}</span>
                    <span>{t(CHANNEL_LABEL[c.channel as NonEmailChannel] ?? c.channel)} · {c.purpose === 'marketing' ? t('маркетинг') : t('сервисные')}</span>
                    <code>{c.source}</code>
                    <span className="muted" title={t('записано {time}', { time: dateTime(c.recorded_at) })}>{dateTime(c.occurred_at)}</span>
                  </summary>
                  <div className="stack" style={{ gap: 4, marginTop: 4 }}>
                    <span className="muted">{t('кто записал: {name}', { name: c.recorded_by })}</span>
                    {Object.keys(c.proof).length ? <pre className="code" style={{ margin: 0 }}>{JSON.stringify(c.proof, null, 2)}</pre> : <span className="muted">{t('доказательство не приложено')}</span>}
                  </div>
                </details>
              ))}
            </div>
          </details>
        )}
        {(data.data?.notifications.length ?? 0) > 0 && (
          <div className="stack" style={{ gap: 4 }}>
            <b className="small">{t('Последние уведомления')}</b>
            {data.data!.notifications.slice(0, 8).map((n) => (
              <Link key={n.id} to={`/notifications/${n.id}`} className="row wrap small" style={{ gap: 6, color: 'inherit' }}>
                <NotificationPill status={n.status} />
                <span>{n.kind === 'verify' ? t('код') : n.stream === 'marketing' ? t('маркетинг') : t('сервисное')}</span>
                <span className="muted">{ago(n.created_at)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
      {consentFor && <ChannelConsentModal contactId={contactId} identity={consentFor} onClose={() => setConsentFor(null)} onSaved={() => { setConsentFor(null); reload(); }} />}
    </div>
  );
}

function ChannelConsentModal({ contactId, identity, onClose, onSaved }: { contactId: string; identity: Identity; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const channels = IDENTITY_CHANNELS[identity.kind];
  const [channel, setChannel] = useState<NonEmailChannel>(channels[0]);
  const [purpose, setPurpose] = useState<'transactional' | 'marketing'>('transactional');
  const [status, setStatus] = useState<'granted' | 'withdrawn'>('granted');
  const [source, setSource] = useState('');
  const [proof, setProof] = useState('');
  async function save() {
    try {
      const r = await api.post<{ warnings?: { message: string }[] }>(`/contacts/${contactId}/channel-consents`, { identity_id: identity.id, channel, purpose, status, source: source.trim(), ...(proof.trim() ? { proof: { text: proof.trim() } } : {}) });
      toast(r.warnings?.[0]?.message ?? t('Записано в журнал согласий'), r.warnings ? 'bad' : 'ok');
      onSaved();
    } catch (err) {
      toast(errText(err), 'bad');
    }
  }
  return (
    <Modal title={t('Согласие · {hint}', { hint: identity.hint })} onClose={onClose} footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-primary" disabled={!source.trim() || (status === 'granted' && proof.trim().length < 3)} onClick={save}>{t('Записать')}</button></>}>
      <Field label={t('Канал')}>
        <div className="chips">{channels.map((c) => <button key={c} type="button" className={`chip${channel === c ? ' on' : ''}`} onClick={() => setChannel(c)}>{t(CHANNEL_LABEL[c])}</button>)}</div>
      </Field>
      <Field label={t('Цель')}>
        <div className="chips">
          <button type="button" className={`chip${purpose === 'transactional' ? ' on' : ''}`} onClick={() => setPurpose('transactional')}>{t('Сервисные')}</button>
          <button type="button" className={`chip${purpose === 'marketing' ? ' on' : ''}`} onClick={() => setPurpose('marketing')}>{t('Маркетинг')}</button>
        </div>
      </Field>
      <div className="chips">
        <button type="button" className={`chip${status === 'granted' ? ' on' : ''}`} onClick={() => setStatus('granted')}>{t('Согласие')}</button>
        <button type="button" className={`chip${status === 'withdrawn' ? ' on' : ''}`} onClick={() => setStatus('withdrawn')}>{t('Отзыв')}</button>
      </div>
      <Field label={t('Источник (обязательно)')} hint={t('Откуда согласие: checkout_form, phone_call, support…')}><input className="input input-mono" value={source} onChange={(e) => setSource(e.target.value)} maxLength={64} /></Field>
      <Field label={t('Доказательство')} hint={status === 'granted' ? t('Обязательно для согласия: как и когда получено') : t('Необязательно')}>
        <textarea className="input" rows={3} placeholder={t('Галочка «Получать SMS о заказе» при оформлении заказа A-1042')} value={proof} onChange={(e) => setProof(e.target.value)} />
      </Field>
      <div className="small muted">{t('Подавление сильнее согласия: новая запись «согласие» его не снимает.')}</div>
    </Modal>
  );
}
